import type { CreateShopModalProps } from "@v2/types/modalTypes";

import React, { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { LabeledControl } from "@replohq/lattice/blocks/labeled-control/LabeledControl";
import { ResponsiveDialog } from "@replohq/lattice/blocks/modal/ResponsiveDialog";
import { Button } from "@replohq/lattice/components/button/Button";
import { Input } from "@replohq/lattice/components/input/Input";
import {
  useCurrentWorkspace,
  useCurrentWorkspaceId,
} from "@v2/contexts/WorkspaceDashboardContext";
import {
  isAgentLaunchCreateFlowDismissed,
  recordAgentLaunchCreateFlowDismissal,
} from "@v2/features/agent-beta/agentLaunchDismissal";
import { AgentLaunchModal } from "@v2/features/agent-beta/AgentLaunchModal";
import { ImportShopifyIntegrationControl } from "@v2/features/new-shop-onboarding/ImportShopifyIntegrationControl";
import { resolveImportShopifyIntegrationId } from "@v2/features/new-shop-onboarding/resolveImportShopifyIntegrationId";
import { useOnboardingCreateProject } from "@v2/features/onboarding/hooks/useOnboardingCreateProject";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import useInstallShopify from "@v2/hooks/useInstallShopify";
import { integrationRouter } from "@v2/trpc/routes/integration";
import { workspaceRouter } from "@v2/trpc/routes/workspace";
import { routes } from "@v2/utils/router";
import { Controller, useForm } from "react-hook-form";
import { Navigate, useNavigate } from "react-router-dom";
import { getNewProjectOnboardingPath } from "replo-utils/lib/agentApplicationPaths";
import { isOsmanthusEnabled } from "replo-utils/lib/osmanthus";
import { z } from "zod";

const createProjectFormSchema = z.object({
  shopName: z.string().trim().min(1, "Enter a name for your project"),
  // undefined = untouched (default to first store); null = explicit "None"
  shopifyIntegrationInstallationId: z.string().nullish(),
});

type CreateProjectFormValues = z.infer<typeof createProjectFormSchema>;

export function CreateShopModal({
  workspaceId: propWorkspaceId,
  title,
  isShopifyPaymentProcessor: isShopifyPaymentProcessorProp,
  open,
  onOpenChange,
}: CreateShopModalProps & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const currentWorkspaceId = useCurrentWorkspaceId();
  const workspace = useCurrentWorkspace();
  const installShopify = useInstallShopify();
  const { createProject, isCreatingProject } = useOnboardingCreateProject();

  const workspaceId = propWorkspaceId ?? currentWorkspaceId;
  const isShopifyPaymentProcessor =
    isShopifyPaymentProcessorProp ?? workspace?.paymentProcessor === "shopify";
  const { data: workspaceData } = workspaceRouter.getById.useQuery(
    { id: workspaceId ?? "" },
    { enabled: Boolean(workspaceId) },
  );
  const targetWorkspace =
    workspaceData?.workspace ??
    (workspace?.id === workspaceId ? workspace : null);
  const isAgentDefault =
    targetWorkspace?.defaultProjectType === "agent" &&
    !isShopifyPaymentProcessor;

  const { control, formState, handleSubmit, register, reset } =
    useForm<CreateProjectFormValues>({
      mode: "onChange",
      defaultValues: {
        shopName: "",
        shopifyIntegrationInstallationId: undefined,
      },
      resolver: zodResolver(createProjectFormSchema),
    });
  // The Shopify install redirects the whole document, so the button stays busy
  // from the click until the browser leaves the page.
  const [isInstallingShopify, setIsInstallingShopify] = useState(false);
  const [hasDeclinedLetter, setHasDeclinedLetter] = useState(false);
  const [isJoiningBeta, setIsJoiningBeta] = useState(false);
  const isLoading = isCreatingProject || isInstallingShopify;

  const isBetaEligible =
    Boolean(workspaceId) && !isAgentDefault && !isShopifyPaymentProcessor;
  // First-time creators see the founder letter first; declining reveals the name form.
  const isLetterOpen =
    open &&
    isBetaEligible &&
    !hasDeclinedLetter &&
    !isAgentLaunchCreateFlowDismissed();
  const isCreatingAgentProject = isAgentDefault || isJoiningBeta;
  const canImportShopifyIntegration =
    isCreatingAgentProject &&
    (workspaceData?.validThemeBuilderWorkspaceShopifyIntegrationsCount ?? 0) >
      0;
  const { data: importableShopifyData, isLoading: isLoadingImportableShopify } =
    integrationRouter.listImportableThemeBuilderShopifyIntegrations.useQuery(
      { workspaceId: workspaceId ?? "" },
      { enabled: Boolean(workspaceId) && canImportShopifyIntegration },
    );
  const importableShopifyIntegrations =
    importableShopifyData?.integrations ?? [];

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      reset();
      setHasDeclinedLetter(false);
      setIsJoiningBeta(false);
      onOpenChange(false);
    }
  };

  const onSubmit = async (data: CreateProjectFormValues) => {
    if (!user || !workspaceId) {
      return;
    }

    const result = await createProject({
      shopName: data.shopName,
      workspaceId,
      hostingType: isCreatingAgentProject ? "replo" : "shopify",
      userEmail: user.email,
      shouldInvalidateUser: true,
      isAgentEnabled: isCreatingAgentProject,
      shopifyIntegrationInstallationId: canImportShopifyIntegration
        ? resolveImportShopifyIntegrationId({
            selectedId: data.shopifyIntegrationInstallationId,
            importableIntegrationIds: importableShopifyIntegrations.map(
              (integration) => integration.id,
            ),
          })
        : null,
    });

    handleOpenChange(false);

    if (isCreatingAgentProject) {
      // The plan chooser is served by the agent app, not this SPA's router,
      // so it needs a document navigation (matching OnboardingAgentSetup).
      window.location.replace(
        `${routes.onboarding.agentBilling}?${new URLSearchParams({
          projectId: result.project.id,
          workspaceId,
        }).toString()}`,
      );
    } else if (isShopifyPaymentProcessor) {
      setIsInstallingShopify(true);
      installShopify({
        cookie: {
          type: "onboarding",
          workspaceId: result.project.ownerWorkspaceId,
          projectId: result.project.id,
          hostingType: "shopify",
        },
      });
    } else {
      navigate(`/editor/${result.project.id}/new`, { replace: true });
    }
  };

  const isAgentNameStep = isCreatingAgentProject && !isAgentDefault;

  if (open && workspaceId && isCreatingAgentProject && isOsmanthusEnabled()) {
    return <Navigate to={getNewProjectOnboardingPath(workspaceId)} />;
  }

  return (
    <>
      <AgentLaunchModal
        open={isLetterOpen}
        source="create-project"
        onEnterBeta={() => {
          setIsJoiningBeta(true);
          setHasDeclinedLetter(true);
        }}
        onDecline={() => {
          recordAgentLaunchCreateFlowDismissal();
          setHasDeclinedLetter(true);
        }}
      />
      <ResponsiveDialog
        open={open && !isLetterOpen}
        onOpenChange={handleOpenChange}
        title={
          isAgentNameStep
            ? "Name your Replo Agent project"
            : title ?? "Create new project"
        }
        size="sm"
        footer={
          <Button
            type="submit"
            form="create-project-form"
            disabled={
              isLoading ||
              !formState.isValid ||
              !workspaceId ||
              (canImportShopifyIntegration && isLoadingImportableShopify)
            }
            loading={isLoading}
            className="w-full"
          >
            {isAgentNameStep ? "Create Agent project" : "Create new project"}
          </Button>
        }
      >
        <form
          id="create-project-form"
          className="flex min-w-0 flex-col gap-4"
          onSubmit={(event) => void handleSubmit(onSubmit)(event)}
        >
          <div className="flex flex-col gap-2">
            <div className="flex justify-center overflow-hidden rounded-xl bg-surface p-4">
              <img
                src="/images/chat/onboarding-hero.svg"
                alt=""
                className="h-auto w-48"
              />
            </div>
            {isBetaEligible && !isCreatingAgentProject && (
              <div className="flex flex-wrap items-center gap-x-1 text-sm text-placeholder">
                <span>Want the new Replo Agent instead?</span>
                <Button
                  type="button"
                  variant="link"
                  disabled={isLoading}
                  aria-label="Join Replo Agent"
                  onClick={() => setIsJoiningBeta(true)}
                >
                  <span className="text-primary underline underline-offset-4">
                    Join Replo Agent
                  </span>
                </Button>
              </div>
            )}
          </div>
          <LabeledControl
            id="project-name"
            label="Project name"
            error={formState.errors.shopName?.message}
            size="base"
          >
            <Input
              id="project-name"
              size="base"
              placeholder="Enter a name for your project"
              invalid={Boolean(formState.errors.shopName)}
              autoFocus
              {...register("shopName")}
            />
          </LabeledControl>
          {canImportShopifyIntegration && workspaceId && (
            <Controller
              name="shopifyIntegrationInstallationId"
              control={control}
              render={({ field }) => (
                <ImportShopifyIntegrationControl
                  integrations={importableShopifyIntegrations}
                  isLoading={isLoadingImportableShopify}
                  value={field.value}
                  onChange={field.onChange}
                  disabled={isLoading}
                />
              )}
            />
          )}
        </form>
      </ResponsiveDialog>
    </>
  );
}
